"use client";

import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Composición y Arreglos",
  points: ["Desarrollo de melodías inspiradoras y letras con propósito.", "Creación musical personalizada que fortalece cada mensaje.", "Producciones adaptadas a diversos géneros, estilos y necesidades."]
}, {
  title: "Producción Musical Profesional",
  points: ["Creación de canciones inspiradoras con calidad profesional.", "Desarrollo de producciones personalizadas para cada necesidad.", "Enfoque creativo para fortalecer el mensaje y su impacto emocional."]
}, {
  title: "Mezcla Creativa",
  points: ["Balance profesional entre voces, música y elementos sonoros.", "Optimización del audio para lograr claridad, profundidad y calidad.", "Integración de recursos modernos al servicio de un mensaje con propósito."]
}, {
  title: "Masterización Final",
  points: ["Optimización del sonido para plataformas digitales y medios modernos.", "Balance y consistencia para una reproducción clara y profesional.", "Entrega de archivos finales listos para transmitir un mensaje de esperanza."]
}];
export default function ElCaminoATuSonido() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224670" id="el-camino-a-tu-sonido" className="relative py-24 bg-background text-foreground overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[100px] -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-secondary/10 rounded-lg blur-[80px] -z-10 pointer-events-none" />

      <div className={`max-w-6xl mx-auto px-4 md:px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
        
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-wider">
            El Camino a tu Sonido
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Un proceso creativo diseñado para transformar tus ideas en una producción musical que inspire, comunique tu mensaje y conecte con quienes la escuchan.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border to-transparent md:-translate-x-1/2" />

          <div className="space-y-16 md:space-y-24">
            {steps.map((step, i) => <div data-index={i} key={i} className="relative flex flex-col md:flex-row items-center gap-8">
                
                {}
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 flex items-center justify-center bg-background py-6 z-10">
                  <span className={`text-6xl md:text-7xl font-default font-bold select-none transition-all duration-500 ${i === 0 ? "text-primary opacity-100 drop-shadow-[0_0_15px_rgba(var(--primary),0.5)] scale-110" : i === 1 ? "text-secondary opacity-100 drop-shadow-[0_0_15px_rgba(var(--secondary),0.5)] scale-110" : i === 2 ? "text-accent-foreground opacity-100 drop-shadow-[0_0_15px_rgba(var(--accent-foreground),0.5)] scale-110" : i === 3 ? "text-secondary opacity-100 drop-shadow-[0_0_20px_rgba(var(--secondary),0.6)] scale-110" : "text-transparent [-webkit-text-stroke:1px_var(--color-primary)] opacity-20"}`}>
                    0{i + 1}
                  </span>
                </div>

                {}
                <div className={`w-full md:w-1/2 pl-24 md:pl-0 ${i % 2 === 0 ? 'md:pr-20' : 'md:pl-20 md:ml-auto'}`}>
                  <Card className={`backdrop-blur-xl transition-all duration-300 relative overflow-hidden group rounded-lg ${i === 0 ? "bg-gradient-to-br from-primary/20 via-card/40 to-secondary/10 border-primary/40 shadow-2xl shadow-primary/20 scale-[1.02] hover:border-primary/60 hover:shadow-lg hover:shadow-primary/30 hover:scale-105" : i === 1 ? "bg-gradient-to-br from-secondary/20 via-card/40 to-primary/10 border-secondary/40 shadow-2xl shadow-secondary/20 scale-[1.02] hover:border-secondary/60 hover:shadow-lg hover:shadow-secondary/30 hover:scale-105" : i === 2 ? "bg-gradient-to-br from-accent/20 via-card/40 to-secondary/10 border-accent-foreground/40 shadow-2xl shadow-accent/20 scale-[1.02] hover:border-accent/60 hover:shadow-lg hover:shadow-accent/30 hover:scale-105" : i === 3 ? "bg-gradient-to-br from-secondary/20 via-card/40 to-accent/10 border-secondary/40 shadow-2xl shadow-secondary/30 scale-[1.02] hover:border-secondary/60 hover:shadow-lg hover:shadow-secondary/40 hover:scale-105" : "bg-card/40 border-border/30 shadow-xl hover:shadow-2xl"}`}>
                    {}
                    <div className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-500 pointer-events-none ${i === 0 ? 'from-primary/10 to-transparent opacity-100' : i === 1 ? 'from-secondary/10 to-transparent opacity-100' : i === 2 ? 'from-accent/10 to-transparent opacity-100' : i === 3 ? 'from-secondary/10 to-transparent opacity-100' : 'from-primary/10 to-transparent opacity-0 group-hover:opacity-100'}`} />
                    
                    <CardContent className="p-6 md:p-8 relative z-10">
                      <h3 className="text-2xl font-default font-semibold text-foreground mb-6 tracking-wide">
                        {step.title}
                      </h3>
                      <ul className="space-y-4">
                        {step.points.map((point, j) => <li data-index={j} key={j} className="flex items-start gap-3">
                            <div className={`mt-1 rounded-lg p-1 shrink-0 transition-colors duration-300 ${i === 0 ? "bg-primary/20" : i === 1 ? "bg-secondary/20" : i === 2 ? "bg-accent/20" : i === 3 ? "bg-secondary/20 group-hover:bg-secondary/30" : "bg-primary/20"}`}>
                              <Check className={`w-4 h-4 ${i === 0 ? "text-primary" : i === 1 ? "text-secondary" : i === 2 ? "text-accent-foreground" : i === 3 ? "text-secondary" : "text-primary"}`} />
                            </div>
                            <span className="text-muted-foreground font-default leading-relaxed">
                              {point}
                            </span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>
                </div>

              </div>)}
          </div>
        </div>

        <div className="mt-24 flex justify-center">
          <Button size="lg" className="rounded-lg px-8 shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-default" asChild>
            <Link to="/ServicesPage">
              Inicia tu Proyecto
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}